import React, { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";

import { CartProvider } from "@/contexts/CartContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/lib/settings";
import { hydrateAwgRate } from "@/lib/api";

import { SiteLayout } from "@/components/layout/SiteLayout";
import HomePage from "@/pages/HomePage";
import ServicesPage from "@/pages/ServicesPage";
import ServiceConfiguratorPage from "@/pages/ServiceConfiguratorPage";
import PackageBuilder from "@/pages/PackageBuilder";
import BrandingSituationGate from "@/pages/BrandingSituationGate";
import ExistingLogoConfigurator from "@/pages/ExistingLogoConfigurator";
import { useSearchParams, useParams } from "react-router-dom";
import PortfolioPage from "@/pages/PortfolioPage";
import PortfolioDetailPage from "@/pages/PortfolioDetailPage";
import LaptopsPage from "@/pages/LaptopsPage";
import SoftwarePage from "@/pages/SoftwarePage";
import SoftwareDetailPage from "@/pages/SoftwareDetailPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CheckoutResultPage from "@/pages/CheckoutResultPage";
import CheckoutProcessingPage from "@/pages/CheckoutProcessingPage";
import EstimateResultPage from "@/pages/EstimateResultPage";
import NotFoundPage from "@/pages/NotFoundPage";
import QreditsPresentationPage from "@/pages/QreditsPresentationPage";
import { useSettings } from "@/lib/settings";
import { initAnalytics, trackPageView } from "@/lib/analytics";
import { buildOrganizationSchema, useJsonLd } from "@/lib/seoSchema";
import { useLocation } from "react-router-dom";
import SetupConfiguratorPage from "@/pages/SetupConfiguratorPage";
import WorkstationConfiguratorPage from "@/pages/WorkstationConfiguratorPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import BrandStrategyReview from "@/pages/admin/BrandStrategyReview";
import BrandPhase5Workspace from "@/pages/admin/BrandPhase5Workspace";
import AuthCallbackPage from "@/pages/AuthCallbackPage";
import BrandIntakePage from "@/pages/BrandIntakePage";
import CustomerDirection from "@/pages/brand/CustomerDirection";
import CustomerConcepts from "@/pages/brand/CustomerConcepts";
import CustomerDelivery from "@/pages/brand/CustomerDelivery";
import DesignerWorkspace from "@/pages/designer/DesignerWorkspace";
import { ScrollToTop } from "@/components/util/ScrollToTop";
import { FaviconSetter } from "@/components/util/FaviconSetter";

// SEO Phase 3 — mounts GA4 (no-op until CMS measurement ID is set) and
// the sitewide Organization JSON-LD schema. Both derive from CMS settings;
// neither renders anything visible.
function SeoPhase3() {
  const s = useSettings();
  const location = useLocation();
  useEffect(() => {
    if (s?.ga4_measurement_id) initAnalytics(s.ga4_measurement_id);
  }, [s?.ga4_measurement_id]);
  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location.pathname, location.search]);
  useJsonLd(buildOrganizationSchema(s));

  // GSC verification meta tag — injected when CMS field is set. Note: for
  // initial verification Google fetches raw HTML; use the HTML file upload
  // method (place google*.html in public/) as the reliable path. This meta
  // tag is belt-and-braces for re-verification.
  useEffect(() => {
    if (!s?.gsc_verification_code || typeof document === "undefined") return undefined;
    const el = document.createElement("meta");
    el.name = "google-site-verification";
    el.content = s.gsc_verification_code;
    document.head.appendChild(el);
    return () => { el.parentNode?.removeChild(el); };
  }, [s?.gsc_verification_code]);

  return null;
}

// Website Repositioning (Sep 2026) — gates the public workstation/software
// routes on the CMS flag `public_commerce_workstations_software_enabled`.
// Strict `=== true`: a cold visitor (empty settings cache) never sees the
// retired catalog — the routes only render once /api/settings confirms the
// flag is ON. While OFF, the existing in-app NotFound page renders.
// NOTE: the static SPA host cannot emit genuine 404/410 HTTP statuses for
// client-side routes (see completion report) — the genuine 410s live on
// the backend API endpoints instead.
function PublicCommerceRoute({ children }) {
  const s = useSettings();
  if (s?.public_commerce_workstations_software_enabled !== true) {
    return <NotFoundPage />;
  }
  return children;
}

function App() {
  // Hydrate the AWG→USD rate from Site Settings once at app boot. Fallback
  // to the 1.79 constant lives inside hydrateAwgRate if the request fails.
  useEffect(() => { hydrateAwgRate(); }, []);
  return (
    <AuthProvider>
      <SettingsProvider>
        <CartProvider>
          <BrowserRouter>
            <SeoPhase3 />
          <ScrollToTop />
          <FaviconSetter />
          <Toaster position="top-right" theme="dark" richColors />
          <Routes>
            <Route element={<SiteLayout />}>
              <Route path="/" element={<HomePage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:slug" element={<ServiceRouteSwitch />} />
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/portfolio/:slug" element={<PortfolioDetailPage />} />
              {/* Workstations — new canonical path. `/laptops` kept as a
                  permanent redirect so old links (SEO backlinks, Calvin's
                  marketing material, cached links) still resolve.
                  Sep 2026: all workstation + software public routes are
                  gated on the CMS public-commerce flag (NotFound while
                  OFF). Components and data are preserved for reactivation. */}
              <Route path="/workstations" element={<PublicCommerceRoute><LaptopsPage /></PublicCommerceRoute>} />
              <Route path="/workstations/:useCaseSlug" element={<PublicCommerceRoute><WorkstationConfiguratorPage /></PublicCommerceRoute>} />
              <Route path="/laptops" element={<PublicCommerceRoute><Navigate to="/workstations" replace /></PublicCommerceRoute>} />
              <Route path="/setups/:slug" element={<PublicCommerceRoute><SetupConfiguratorPage /></PublicCommerceRoute>} />
              <Route path="/software" element={<PublicCommerceRoute><SoftwarePage /></PublicCommerceRoute>} />
              <Route path="/software/:slug" element={<PublicCommerceRoute><SoftwareDetailPage /></PublicCommerceRoute>} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/checkout" element={<CheckoutPage />} />
              <Route path="/checkout/processing" element={<CheckoutProcessingPage />} />
              <Route path="/checkout/success" element={<CheckoutResultPage status="success" />} />
              <Route path="/checkout/cancel" element={<CheckoutResultPage status="cancel" />} />
              <Route path="/estimate/received" element={<EstimateResultPage />} />
              {/* Catch-all — rendered 404 page with noindex (see NotFoundPage). */}
              <Route path="*" element={<NotFoundPage />} />
            </Route>
            {/* Private, unlisted presentation — no site chrome, noindex, key-gated. */}
            <Route path="/p/qredits-aruba" element={<QreditsPresentationPage />} />
            <Route path="/admin" element={<AdminLoginPage />} />
            <Route path="/admin/auth-callback" element={<AuthCallbackPage />} />
            <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
            <Route path="/admin/brand-projects/:projectId/strategy" element={<BrandStrategyReview />} />
            <Route path="/admin/brand-projects/:projectId/phase5" element={<BrandPhase5Workspace />} />
            <Route path="/designer" element={<DesignerWorkspace />} />
            <Route path="/brand/:projectId/direction" element={<CustomerDirection />} />
            <Route path="/brand/:projectId/concepts" element={<CustomerConcepts />} />
            <Route path="/brand/:projectId/delivery" element={<CustomerDelivery />} />
            {/* UR Brand System — Phase 2 customer intake. Standalone
                route (not nested in SiteLayout) so the intake is a
                focused flow, matching checkout/estimate conventions. */}
            <Route path="/brand-intake" element={<BrandIntakePage />} />
          </Routes>
        </BrowserRouter>
        </CartProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;

// Route switch: `?legacy=1` renders the pre-Phase-3 hardcoded configurator so
// old vs new can be compared side-by-side on the same live services.
// Also handles 301-equivalent client-side redirects for renamed slugs so old
// bookmarks and indexed URLs keep resolving to the new URL.
const SERVICE_SLUG_REDIRECTS = {
  "cocial-campaign-visuals": "content-campaign-visuals",
};

function ServiceRouteSwitch() {
  const [params] = useSearchParams();
  const { slug } = useParams();
  const redirectTo = SERVICE_SLUG_REDIRECTS[slug];
  if (redirectTo) {
    const qs = params.toString();
    return <Navigate to={`/services/${redirectTo}${qs ? `?${qs}` : ""}`} replace />;
  }
  // Logo & Brand Identity uses a situation-first entry gate. The customer
  // picks a path (?path=new | ?path=existing) before landing in the
  // configurator, so a graphic designer never has to explain which
  // package they need. Absent path → show the 3-tile gate.
  if (slug === "logo-brand-identity") {
    const path = params.get("path");
    if (path === "existing") return <ExistingLogoConfigurator />;
    if (path !== "new") return <BrandingSituationGate />;
    // path === "new" falls through to the standard PackageBuilder below.
  }
  return params.get("legacy") === "1" ? <ServiceConfiguratorPage /> : <PackageBuilder />;
}
